import {ChatBubbleOutlineOutlined, ErrorOutline, MarkEmailRead} from "@mui/icons-material";
import {
  Box,
  Divider,
  List,
  Stack,
  Typography
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Skeletons from "../../../components/loader/Skeletons";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import useUser from "../../../hooks/useUser";
import { isArrayEmpty } from "../../../utils/Functions";
import useGetConvos from "../hooks/useGetConvos";
import Conversation from "./Conversation";
import ConversationFilter from "./ConversationFilter";
import OpenConversation from "./OpenConversation";

export default function ConversationSidebar() {
  const { data, isLoading: loadingConvos, error } = useGetConvos();
  const dataEmpty = isArrayEmpty(data) || data == undefined || data == null;

  const [searchParams] = useSearchParams();
  const readF = searchParams.get("read_f");
  const isReadFilter = readF === null ? true : readF === "true";

  const allConvos = data ?? [];
  const unreadConvos = data?.filter(conv => !conv?.read) ?? [];

  if (loadingConvos) {
    return <Loader />;
  } else if (!loadingConvos && error) {
    return <Error />;
  } else if (!loadingConvos && dataEmpty) {
    return <NoData isReadFilter={isReadFilter}/>;
  }

  const convosToShow = isReadFilter ? allConvos : unreadConvos;
  const noConvos = convosToShow?.length === 0;

  return (
    <Wrapper>
      {noConvos ? (
        <NoConvosIcon isReadFilter={isReadFilter} />
      ) : (
        <ConvoList convos={convosToShow} />
      )}
    </Wrapper>
  );
}

const ConversationItem = React.memo(function ConversationItem({ conv }) {
  return <Conversation conv={conv} />;
});

function ConvoList({convos = []}) {
  return (
    <List sx={{ flexGrow: 1, overflowY: "auto" }}>
      {convos?.map(conv => (
        <ConversationItem key={conv?.id} conv={conv} />
      ))}
    </List>
  )
}

function Loader() {
  return (
    <Wrapper>
      <Skeletons numOfSkeletons={6} grid={false} />
    </Wrapper>
  );
}

function NoConvosIcon({isReadFilter = true}) {
  const { t } = useTranslation();
  const { roles } = useUser();
  const color = usePrimaryColor();

  return (
    <Stack
      spacing={2}
      alignItems="center"
      justifyContent="center"
      sx={{ flex: 1, py: 6, px: 2 }}
    >
      {isReadFilter == false ? (
        <MarkEmailRead sx={{ color: color }} fontSize="large" />
      ) : (
        <ChatBubbleOutlineOutlined sx={{ color: color }} fontSize="large" />
      )}
      <Typography variant="h6" color="text.secondary">
        {t(isReadFilter == false ? "AllMessagesHaveBeenRead" : "NoConversationsYet")}
      </Typography>
      {roles.user ? null : (
        <Typography variant="body2" color="text.secondary" align="center">
          {t(isReadFilter == false ? "" : "ClickinOnPlusToStartNewConvo")}
        </Typography>
      )}
    </Stack>
  );
}

function NoData({isReadFilter = false}) {
   return (
    <Wrapper>
      <NoConvosIcon isReadFilter={isReadFilter}/>
    </Wrapper>
  );
}

function Error() {
  const { t } = useTranslation();
  return (
    <Wrapper>
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ flex: 1, py: 6, px: 2 }}
      >
        <ErrorOutline color="error" fontSize="large" />
        <Typography align="center" variant="h6" color="error">
          {t("SomethingWentWrong")}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {t("UnableToLoadConvos")}
        </Typography>
      </Stack>
    </Wrapper>
  );
}

function Wrapper({ children, filter, setFilter }) {
  const color = usePrimaryColor();
  const { t } = useTranslation();

  const [width, setWidth] = React.useState(
    localStorage.getItem("inbox_width") ?? 350
  );
  const resizerRef = React.useRef(null);
  const isDraggingRef = React.useRef(false);

  const startDragging = (e) => {
    isDraggingRef.current = true;
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", stopDragging);
  };

  const onDrag = (e) => {
    if (!isDraggingRef.current) return;
    const newWidth =
      e.clientX - resizerRef.current.getBoundingClientRect().left;
    if (newWidth > 280 && newWidth < 650) {
      localStorage.setItem("inbox_width", newWidth);
      setWidth(newWidth);
    }
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
    document.removeEventListener("mousemove", onDrag);
    document.removeEventListener("mouseup", stopDragging);
  };

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <Box
        sx={{
          width,
          borderRight: `1px solid ${color}`,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          userSelect: "none",
        }}
        ref={resizerRef}
      >
        <Stack
          p={1}
          justifyContent={"space-between"}
          alignItems={"center"}
          direction={"row"}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {t("Conversations")}
          </Typography>
          <OpenConversation />
        </Stack>
        <ConversationFilter />
        <Divider />
        {children}

        <Box
          onMouseDown={startDragging}
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: "5px",
            cursor: "col-resize",
            zIndex: 10,
          }}
        />
      </Box>
    </Box>
  );
}
